import React, { useState, useEffect, useMemo } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  BarChart3, Users, FolderOpen, Gauge, TrendingUp, Download, PieChart as PieIcon,
  History, CheckCircle2, PlusCircle, CalendarDays, LineChart, Pencil, FolderHeart,
  ArrowRightLeft, FileText, Activity,
} from 'lucide-react';
import { workItemsApi, driversApi, reportingApi, projectsApi, exportApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type {
  WorkItem, AnnualStrategicDriver, WorkItemType, Priority, Project, StructuredKpi,
  TimeAllocationReport, ActivityFeedItem, DelegationReport,
} from '../types/models';
import HeadlineInsight from '../components/intelligence/HeadlineInsight';
import {
  deriveCapacityInsight,
  deriveDelegationInsight,
  deriveExecutiveInsight,
  deriveTimeAllocationInsight,
} from '../utils/intelligence';
import WeeklyExportDialog from '../components/reports/WeeklyExportDialog';
import { weeklyNarrativeFromWorkItems } from '../utils/weeklyNarrative';
import { reportsCopy } from '../copy/reports';
import { authApi } from '../api/services';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ReportsPage — the heaviest reporting surface. Eight tabs (My Reports,
// Capacity, Strategic Drivers, Projects, Time Allocation, Activity,
// Delegation, Cross-Executive). Recharts pies + bars throughout; the rest
// is native tables + tinted chips + bar fills.
// ---------------------------------------------------------------------------

// Type/priority palettes — kept as literal hex so the recharts pies pick up
// stable colours regardless of theme. Tone chips elsewhere use Tailwind
// classes derived from these.
const TYPE_COLORS: Record<string, string> = {
  Task: '#6C9BFF',
  Decision: '#FF8A65',
  Briefing: '#69F0AE',
  Review: '#FFB74D',
  FollowUp: '#CE93D8',
  Draft: '#4DD0E1',
  Reading: '#AED581',
};

const PRIORITY_COLORS: Record<Priority, string> = {
  Critical: '#FF5252',
  High: '#FF8A65',
  Medium: '#6C9BFF',
  Low: '#69F0AE',
};

// Theme-aware chart colours via CSS variables — picks up dark/light from the
// active theme rather than reading from MUI's theme object.
const C = {
  primary: 'hsl(var(--primary))',
  secondary: '#CE93D8',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  textPrimary: 'hsl(var(--foreground))',
  textSecondary: 'hsl(var(--muted-foreground))',
  bgPaper: 'hsl(var(--background))',
  divider: 'hsl(var(--border))',
  tooltipBg: 'hsl(var(--background))',
  gridLine: 'hsl(var(--border))',
};

type DateRange = 'week' | 'month' | 'lastMonth';

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg border border-border bg-background p-2 text-sm text-foreground"
      style={{ minWidth: 120 }}
    >
      {label && <p className="mb-1 text-xs text-muted-foreground">{label}</p>}
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-sm" style={{ color: entry.color || 'inherit' }}>
          {entry.name}: {entry.value}
          {typeof entry.value === 'number' && entry.name?.includes('Min') ? 'm' : ''}
        </p>
      ))}
    </div>
  );
}

function getDateRange(range: DateRange): { start: Date; end: Date } {
  const now = new Date();
  if (range === 'week') {
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const start = new Date(now);
    start.setDate(now.getDate() - diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (range === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { start, end };
}

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill={C.textPrimary} textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Range toggle — used at the top of multiple tabs.
function RangeToggle({ value, onChange }: { value: DateRange; onChange: (v: DateRange) => void }) {
  const options: { value: DateRange; label: string }[] = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Date range"
      className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
    >
      {options.map(opt => {
        const checked = value === opt.value;
        return (
          <Button
            key={opt.value}
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            size="sm"
            variant={checked ? 'default' : 'ghost'}
            onClick={() => onChange(opt.value)}
            className="h-7 px-3 text-xs"
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-3 text-base font-semibold">{children}</h3>;
}

// ===========================================================================
// Main ReportsPage component
// ===========================================================================

export default function ReportsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<WorkItem[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<DateRange>('month');
  const [activeTab, setActiveTab] = useState('0');
  const [capacityForecast, setCapacityForecast] = useState<any>(null);
  const [pdfExportEnabled, setPdfExportEnabled] = useState<boolean | null>(null);
  const [weeklyExportOpen, setWeeklyExportOpen] = useState(false);
  const canSeeCrossExec = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.role === 'ExecutiveAssistant';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [itemsData, driversData] = await Promise.all([
          workItemsApi.getAll(),
          driversApi.getActive(),
        ]);
        setItems(itemsData);
        setDrivers(driversData);
      } catch (err) {
        console.error('Failed to load report data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    reportingApi.getCapacityForecast(4).then(setCapacityForecast).catch(() => {});
    authApi.me()
      .then((me: any) => setPdfExportEnabled(Boolean(me?.features?.pdfExportEnabled)))
      .catch(() => setPdfExportEnabled(false));
  }, []);

  const filteredItems = useMemo(() => {
    const { start, end } = getDateRange(range);
    return items.filter(item => {
      const d = new Date(item.targetDate);
      return d >= start && d <= end;
    });
  }, [items, range]);

  const timeAllocation = useMemo(() => {
    const focusMinutes = filteredItems
      .filter(i => i.type === 'Task' || i.type === 'Draft' || i.type === 'Reading')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const meetingMinutes = filteredItems
      .filter(i => i.type === 'Briefing' || i.type === 'Review')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const plannedMinutes = filteredItems
      .filter(i => i.status === 'Planned' || i.status === 'InProgress')
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    const overflowMinutes = filteredItems
      .filter(i => i.status === 'Deferred' || i.isCarryForward)
      .reduce((s, i) => s + i.estimatedMinutes, 0);
    return [
      { name: 'Focus Time', value: focusMinutes, color: '#6C9BFF' },
      { name: 'Meetings', value: meetingMinutes, color: C.secondary },
      { name: 'Planned Work', value: plannedMinutes, color: C.success },
      { name: 'Overflow', value: overflowMinutes, color: C.error },
    ].filter(d => d.value > 0);
  }, [filteredItems]);

  const typeBreakdown = useMemo(() => {
    const allTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
    return allTypes.map(type => {
      const ofType = filteredItems.filter(i => i.type === type);
      return {
        name: type,
        count: ofType.length,
        minutes: ofType.reduce((s, i) => s + i.estimatedMinutes, 0),
        color: TYPE_COLORS[type],
      };
    }).filter(d => d.count > 0);
  }, [filteredItems]);

  const priorityDistribution = useMemo(() => {
    const allPriorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
    return allPriorities.map(p => ({
      name: p,
      value: filteredItems.filter(i => i.priority === p).length,
      color: PRIORITY_COLORS[p],
    })).filter(d => d.value > 0);
  }, [filteredItems]);

  const timeHogs = useMemo(
    () => [...filteredItems].sort((a, b) => b.estimatedMinutes - a.estimatedMinutes).slice(0, 10),
    [filteredItems],
  );

  const strategicAlignment = useMemo(() => {
    const linked = filteredItems.filter(i => i.strategicDriverId).length;
    const unlinked = filteredItems.length - linked;
    return [
      { name: 'Linked to Strategy', value: linked, color: '#6C9BFF' },
      { name: 'Unlinked', value: unlinked, color: '#8892A8' },
    ].filter(d => d.value > 0);
  }, [filteredItems]);

  const carryForwardData = useMemo(() => {
    const cfItems = filteredItems.filter(i => i.isCarryForward);
    const counts: Record<string, { title: string; count: number; minutes: number }> = {};
    cfItems.forEach(item => {
      const key = item.title;
      if (!counts[key]) counts[key] = { title: key, count: 0, minutes: item.estimatedMinutes };
      counts[key].count += 1;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [filteredItems]);

  if (!user) return null;

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      if (activeTab === '0') {
        const r = await exportApi.downloadWorkItemsCsv();
        triggerDownload(r.data, `work-items-${today}.csv`);
      } else if (activeTab === '2') {
        const r = await exportApi.downloadKpiHistoryCsv();
        triggerDownload(r.data, `kpi-history-${today}.csv`);
      } else if (activeTab === '3') {
        const r = await exportApi.downloadProjectsCsv();
        triggerDownload(r.data, `projects-${today}.csv`);
      }
    } catch {
      alert('Failed to export data.');
    }
  };

  const showExportButton = activeTab === '0' || activeTab === '2' || activeTab === '3';

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
        </div>
        <div className="flex items-center gap-2">
          {showExportButton && (
            <Button size="sm" variant="outline" onClick={handleExportCsv} className="gap-1.5">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}
          {activeTab === '0' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setWeeklyExportOpen(true)}
                    disabled={!pdfExportEnabled}
                    className="gap-1.5"
                  >
                    <FileText className="h-4 w-4" />
                    {reportsCopy.exportPdf}
                  </Button>
                </span>
              </TooltipTrigger>
              {pdfExportEnabled === false && (
                <TooltipContent>{reportsCopy.pdfDisabledHeading}</TooltipContent>
              )}
            </Tooltip>
          )}
          {activeTab === '0' && <RangeToggle value={range} onChange={setRange} />}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="0">My Reports</TabsTrigger>
          <TabsTrigger value="1" className="gap-1.5"><Gauge className="h-4 w-4" />Capacity</TabsTrigger>
          <TabsTrigger value="2" className="gap-1.5"><TrendingUp className="h-4 w-4" />Strategic Drivers</TabsTrigger>
          <TabsTrigger value="3" className="gap-1.5"><FolderOpen className="h-4 w-4" />Projects</TabsTrigger>
          <TabsTrigger value="4" className="gap-1.5"><PieIcon className="h-4 w-4" />Time Allocation</TabsTrigger>
          <TabsTrigger value="5" className="gap-1.5"><History className="h-4 w-4" />Activity</TabsTrigger>
          <TabsTrigger value="6" className="gap-1.5"><ArrowRightLeft className="h-4 w-4" />Delegation</TabsTrigger>
          {canSeeCrossExec && (
            <TabsTrigger value="7" className="gap-1.5"><Users className="h-4 w-4" />Cross-Executive</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {activeTab === '0' && (
        <>
          <HeadlineInsight
            result={deriveExecutiveInsight(
              filteredItems.length === 0
                ? { totalPlanned: 0, done: 0, carried: 0, longCarry: 0 }
                : (() => {
                    const done = filteredItems.filter((i) => i.status === 'Done').length;
                    const carried = filteredItems.filter((i) => i.isCarryForward).length;
                    const fourDaysMs = 4 * 24 * 60 * 60 * 1000;
                    const now = Date.now();
                    const longCarry = filteredItems.filter(
                      (i) => i.isCarryForward && i.originalDate && now - new Date(i.originalDate).getTime() >= fourDaysMs,
                    ).length;
                    return {
                      totalPlanned: filteredItems.length,
                      done,
                      carried,
                      longCarry,
                    };
                  })(),
            )}
          />
          {loading && (
            <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/3 animate-pulse bg-primary" />
            </div>
          )}

          {!loading && filteredItems.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-base font-semibold text-muted-foreground">No work items found for this period</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Adjust the date range or create work items to see analytics.
                </p>
              </CardContent>
            </Card>
          )}

          {!loading && filteredItems.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Time Allocation Pie */}
              <Card className="h-full">
                <CardContent>
                  <SectionTitle>Time Allocation</SectionTitle>
                  {timeAllocation.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={timeAllocation}
                          cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={3}
                          dataKey="value" labelLine={false} label={renderCustomLabel}
                        >
                          {timeAllocation.map((entry, index) => (
                            <Cell key={index} fill={entry.color} stroke="transparent" />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="py-8 text-center text-sm text-muted-foreground">No time data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Priority Distribution */}
              <Card className="h-full">
                <CardContent>
                  <SectionTitle>Priority Distribution</SectionTitle>
                  {priorityDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={priorityDistribution}
                          cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={3}
                          dataKey="value" labelLine={false} label={renderCustomLabel}
                        >
                          {priorityDistribution.map((entry, index) => (
                            <Cell key={index} fill={entry.color} stroke="transparent" />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="py-8 text-center text-sm text-muted-foreground">No priority data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Work Item Breakdown */}
              <Card className="h-full">
                <CardContent>
                  <SectionTitle>Work Item Breakdown</SectionTitle>
                  {typeBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={typeBreakdown} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} horizontal={false} />
                        <XAxis type="number" tick={{ fill: C.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fill: C.textSecondary, fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                        <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                        <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]} barSize={16}>
                          {typeBreakdown.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                        </Bar>
                        <Bar dataKey="minutes" name="Est. Minutes" radius={[0, 4, 4, 0]} barSize={16} fillOpacity={0.5}>
                          {typeBreakdown.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="py-8 text-center text-sm text-muted-foreground">No type data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Strategic Alignment */}
              <Card className="h-full">
                <CardContent>
                  <SectionTitle>Strategic Alignment</SectionTitle>
                  {strategicAlignment.length > 0 ? (
                    <div>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={strategicAlignment}
                            cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}
                            dataKey="value" labelLine={false} label={renderCustomLabel}
                          >
                            {strategicAlignment.map((entry, index) => (
                              <Cell key={index} fill={entry.color} stroke="transparent" />
                            ))}
                          </Pie>
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Legend formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>} />
                        </PieChart>
                      </ResponsiveContainer>
                      {drivers.length > 0 && (
                        <div className="mt-2 flex flex-col gap-2">
                          {drivers.map(d => (
                            <div key={d.id}>
                              <div className="mb-1 flex justify-between">
                                <p className="text-xs text-muted-foreground">{d.title}</p>
                                <p className="text-xs font-semibold text-primary">{d.progressPercentage}%</p>
                              </div>
                              <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                                <div className="h-full bg-primary transition-all" style={{ width: `${d.progressPercentage}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="py-8 text-center text-sm text-muted-foreground">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Time Hogs Table */}
              <div className="md:col-span-2">
                <Card>
                  <CardContent>
                    <SectionTitle>Time Hogs</SectionTitle>
                    <p className="mb-3 text-sm text-muted-foreground">Top 10 most time-consuming work items</p>
                    <div className="overflow-hidden rounded-lg border border-border">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40">
                          <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            <th className="px-3 py-2">Title</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">Priority</th>
                            <th className="px-3 py-2 text-right">Est. Minutes</th>
                            <th className="px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timeHogs.map(item => {
                            const isHigh = item.estimatedMinutes > 90;
                            return (
                              <tr
                                key={item.id}
                                className={cn(
                                  'border-b border-border last:border-b-0',
                                  isHigh && 'bg-rose-500/[0.06]',
                                )}
                              >
                                <td className={cn('px-3 py-2', isHigh && 'font-semibold')}>
                                  {item.title}
                                  {isHigh && (
                                    <span className="ml-2 inline-flex items-center rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:text-rose-400">
                                      90m+
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                                    style={{ backgroundColor: `${TYPE_COLORS[item.type]}20`, color: TYPE_COLORS[item.type] }}
                                  >
                                    {item.type}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                                    style={{ backgroundColor: `${PRIORITY_COLORS[item.priority]}20`, color: PRIORITY_COLORS[item.priority] }}
                                  >
                                    {item.priority}
                                  </span>
                                </td>
                                <td className={cn('px-3 py-2 text-right font-semibold', isHigh && 'text-rose-600 dark:text-rose-400')}>
                                  {item.estimatedMinutes}m
                                </td>
                                <td className="px-3 py-2">
                                  <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                    {item.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {timeHogs.length === 0 && (
                            <tr>
                              <td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Carry-Forward Frequency */}
              <div className="md:col-span-2">
                <Card>
                  <CardContent>
                    <SectionTitle>Carry-Forward Frequency</SectionTitle>
                    <p className="mb-3 text-sm text-muted-foreground">
                      Items that keep getting pushed: potential blockers requiring attention.
                    </p>
                    {carryForwardData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={Math.max(200, carryForwardData.length * 40 + 40)}>
                        <BarChart data={carryForwardData} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} horizontal={false} />
                          <XAxis type="number" tick={{ fill: C.textSecondary, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <YAxis
                            type="category" dataKey="title"
                            tick={{ fill: C.textSecondary, fontSize: 11 }}
                            axisLine={false} tickLine={false} width={180}
                            tickFormatter={(value: string) => value.length > 28 ? value.substring(0, 25) + '...' : value}
                          />
                          <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                          <Bar dataKey="count" name="Times Carried Forward" fill={C.warning} radius={[0, 4, 4, 0]} barSize={16} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        No carry-forward items in this period
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Capacity Forecast */}
              {capacityForecast && (capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : [])).length > 0 && (
                <div className="md:col-span-2">
                  <Card>
                    <CardContent>
                      <SectionTitle>Capacity Forecast</SectionTitle>
                      <p className="mb-3 text-sm text-muted-foreground">Planned vs available minutes for the next 4 weeks</p>
                      <div className="flex flex-col gap-3">
                        {(capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : [])).map((week: any, i: number) => {
                          const utilisation = week.utilisationPercent ?? (week.availableMinutes > 0 ? Math.round((week.plannedMinutes / week.availableMinutes) * 100) : 0);
                          const isOverloaded = week.isOverloaded ?? utilisation > 100;
                          return (
                            <div key={week.weekStart || i}>
                              <div className="mb-1 flex justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {week.weekStart || week.weekLabel} to {week.weekEnd || ''}
                                </p>
                                <p className={cn('text-xs font-semibold', isOverloaded && 'text-rose-600 dark:text-rose-400')}>
                                  {utilisation}%
                                </p>
                              </div>
                              <div className="mb-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                                <div
                                  className={cn('h-full transition-all', isOverloaded ? 'bg-rose-500' : 'bg-primary')}
                                  style={{ width: `${Math.min(100, utilisation)}%` }}
                                />
                              </div>
                              <p className={cn('text-xs', isOverloaded ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground')}>
                                {week.plannedMinutes}min planned / {week.availableMinutes}min available
                                {isOverloaded && ', overloaded'}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === '1' && <CapacityTab capacityForecast={capacityForecast} />}
      {activeTab === '2' && <StrategicDriversTab drivers={drivers} />}
      {activeTab === '3' && <ProjectsTab drivers={drivers} workItems={items} />}
      {activeTab === '4' && <TimeAllocationTab />}
      {activeTab === '5' && <ActivityFeedTab />}
      {activeTab === '6' && <DelegationTab />}
      {activeTab === '7' && canSeeCrossExec && <CrossExecutiveTab />}

      <WeeklyExportDialog
        open={weeklyExportOpen}
        onClose={() => setWeeklyExportOpen(false)}
        date={new Date().toISOString().slice(0, 10)}
        suggestion={weeklyNarrativeFromWorkItems(filteredItems, { keyRiskCount: 0 })}
      />
    </div>
  );
}

// ===========================================================================
// Capacity Tab
// ===========================================================================

function CapacityTab({ capacityForecast }: { capacityForecast: any }) {
  const weeks: any[] = capacityForecast?.weeks || (Array.isArray(capacityForecast) ? capacityForecast : []);

  const getUtilisation = (week: any): number => {
    if (week.utilisationPercent != null) return week.utilisationPercent;
    if (week.availableMinutes > 0) return Math.round((week.plannedMinutes / week.availableMinutes) * 100);
    return 0;
  };

  const getStatus = (utilisation: number): { label: string; bar: string; chip: string } => {
    if (utilisation > 90) return { label: 'Overloaded', bar: 'bg-rose-500', chip: 'bg-rose-500/15 text-rose-700 dark:text-rose-400' };
    if (utilisation >= 70) return { label: 'Busy', bar: 'bg-amber-500', chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' };
    return { label: 'Good', bar: 'bg-emerald-500', chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' };
  };

  const avgUtilisation = weeks.length > 0
    ? Math.round(weeks.reduce((sum, w) => sum + getUtilisation(w), 0) / weeks.length)
    : 0;

  const toHours = (minutes: number) => Math.round((minutes / 60) * 10) / 10;

  if (!capacityForecast || weeks.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Gauge className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="text-base font-semibold text-muted-foreground">No capacity forecast data available</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Capacity forecast data will appear once you have active plans and calendar events.
          </p>
        </CardContent>
      </Card>
    );
  }

  const avgStatus = getStatus(avgUtilisation);
  const insight = deriveCapacityInsight(weeks);

  return (
    <div>
      <HeadlineInsight result={insight} />
      <Card className="mb-4">
        <CardContent className="flex items-center gap-3 py-4">
          <div
            className={cn(
              'grid h-14 w-14 place-items-center rounded-full border-2 text-base font-bold',
              avgStatus.bar.replace('bg-', 'border-'),
            )}
            style={{ background: 'transparent' }}
          >
            <span className={cn('text-base font-bold', avgStatus.chip.split(' ').filter(c => c.startsWith('text')).join(' '))}>
              {avgUtilisation}%
            </span>
          </div>
          <div>
            <p className="text-base font-semibold">Average utilisation: {avgUtilisation}% over next {weeks.length} weeks</p>
            <p className="text-sm">
              Status: <span className={cn('font-semibold', avgStatus.chip.split(' ').filter(c => c.startsWith('text')).join(' '))}>{avgStatus.label}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <h3 className="mb-3 text-base font-semibold">Capacity forecast: next {weeks.length} weeks</h3>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Week Start</th>
                  <th className="px-3 py-2 text-right">Available</th>
                  <th className="px-3 py-2 text-right">Meeting</th>
                  <th className="px-3 py-2 text-right">Planned Work</th>
                  <th className="px-3 py-2 text-right">Utilisation</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((week: any, i: number) => {
                  const utilisation = getUtilisation(week);
                  const status = getStatus(utilisation);
                  const weekLabel = week.weekStart || week.weekLabel || `Week ${i + 1}`;
                  return (
                    <tr key={weekLabel} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2 font-medium">{weekLabel}</td>
                      <td className="px-3 py-2 text-right">{toHours(week.availableMinutes || 0)}h</td>
                      <td className="px-3 py-2 text-right">{toHours(week.meetingMinutes || 0)}h</td>
                      <td className="px-3 py-2 text-right">{toHours(week.plannedMinutes || 0)}h</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-1.5 w-[60px] overflow-hidden rounded-full bg-muted">
                            <div className={cn('h-full transition-all', status.bar)} style={{ width: `${Math.min(100, utilisation)}%` }} />
                          </div>
                          <p className="min-w-[40px] text-right text-sm font-semibold">{utilisation}%</p>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', status.chip)}>
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// Strategic Drivers Tab
// ===========================================================================

function StrategicDriversTab({ drivers }: { drivers: AnnualStrategicDriver[] }) {
  const { user } = useAuth();
  const canSeeAlignment = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.role === 'ExecutiveAssistant';
  const [alignmentData, setAlignmentData] = useState<any[]>([]);
  const [alignmentLoading, setAlignmentLoading] = useState(false);

  useEffect(() => {
    if (!canSeeAlignment) return;
    setAlignmentLoading(true);
    driversApi.getAlignment()
      .then((data: any) => setAlignmentData(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setAlignmentLoading(false));
  }, [canSeeAlignment]);

  const alignmentByExec = useMemo(() => {
    const groups: Record<string, { name: string; drivers: any[] }> = {};
    alignmentData.forEach((d: any) => {
      if (!groups[d.executiveUserId]) groups[d.executiveUserId] = { name: d.executiveName, drivers: [] };
      groups[d.executiveUserId].drivers.push(d);
    });
    return Object.values(groups);
  }, [alignmentData]);

  const alignedDriverTitles = useMemo(() => {
    if (alignmentByExec.length < 2) return new Set<string>();
    const titleMap: Record<string, string[]> = {};
    alignmentData.forEach((d: any) => {
      const normalized = d.title.toLowerCase().trim();
      if (!titleMap[normalized]) titleMap[normalized] = [];
      if (!titleMap[normalized].includes(d.executiveUserId)) titleMap[normalized].push(d.executiveUserId);
    });
    const aligned = new Set<string>();
    Object.entries(titleMap).forEach(([title, execs]) => {
      if (execs.length > 1) aligned.add(title);
    });
    return aligned;
  }, [alignmentByExec, alignmentData]);

  if (drivers.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <TrendingUp className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="text-base font-semibold text-muted-foreground">No active strategic drivers</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure strategic drivers to see KPI tracking and progress here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const KPI_TONE: Record<string, { chip: string; bar: string }> = {
    Ahead: { chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', bar: 'bg-emerald-500' },
    OnTrack: { chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', bar: 'bg-emerald-500' },
    Behind: { chip: 'bg-rose-500/15 text-rose-700 dark:text-rose-400', bar: 'bg-rose-500' },
    AtRisk: { chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', bar: 'bg-amber-500' },
    Unknown: { chip: 'bg-muted text-muted-foreground', bar: 'bg-muted-foreground' },
  };

  const getKpiStatus = (kpi: StructuredKpi): { label: string; tone: typeof KPI_TONE.Ahead } => {
    const key = kpi.status === 'Ahead' || kpi.status === 'OnTrack' || kpi.status === 'Behind' || kpi.status === 'AtRisk'
      ? kpi.status
      : 'Unknown';
    return { label: key === 'OnTrack' ? 'On Track' : key === 'AtRisk' ? 'At Risk' : key, tone: KPI_TONE[key] };
  };

  const getDirectionArrow = (direction?: string): string => {
    if (direction === 'Up') return '↑';
    if (direction === 'Down') return '↓';
    if (direction === 'Maintain') return '→';
    return '';
  };

  const getDriverRag = (driver: AnnualStrategicDriver) => {
    const kpis = driver.structuredKpis;
    if (!kpis || kpis.length === 0) {
      if (driver.progressPercentage >= 60) return { chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', bar: 'bg-emerald-500', label: 'Green' };
      if (driver.progressPercentage >= 30) return { chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', bar: 'bg-amber-500', label: 'Amber' };
      return { chip: 'bg-rose-500/15 text-rose-700 dark:text-rose-400', dot: 'bg-rose-500', bar: 'bg-rose-500', label: 'Red' };
    }
    const behindCount = kpis.filter(k => k.status === 'Behind' || k.status === 'AtRisk').length;
    if (behindCount > kpis.length / 2) return { chip: 'bg-rose-500/15 text-rose-700 dark:text-rose-400', dot: 'bg-rose-500', bar: 'bg-rose-500', label: 'Red' };
    if (behindCount > 0) return { chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', bar: 'bg-amber-500', label: 'Amber' };
    return { chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', bar: 'bg-emerald-500', label: 'Green' };
  };

  const getKpiHealthTone = (health: string) => {
    if (health === 'Green') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    if (health === 'Amber') return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
    return 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
  };

  return (
    <div>
      <div className="flex flex-col gap-4">
        {drivers.map(driver => {
          const rag = getDriverRag(driver);
          const kpis = driver.structuredKpis || [];
          return (
            <Card key={driver.id}>
              <CardContent>
                <div className="mb-3 flex items-center gap-2">
                  <span aria-hidden className={cn('h-3 w-3 shrink-0 rounded-full', rag.dot)} />
                  <p className="flex-1 text-base font-semibold">{driver.title}</p>
                  <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', rag.chip)}>
                    {rag.label}
                  </span>
                  <p className="text-sm font-semibold text-primary">{driver.progressPercentage}%</p>
                </div>
                <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className={cn('h-full transition-all', rag.bar)} style={{ width: `${Math.min(100, driver.progressPercentage)}%` }} />
                </div>
                {kpis.length > 0 ? (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2">KPI</th>
                          <th className="px-3 py-2 text-right">Current</th>
                          <th className="px-3 py-2 text-right">Target</th>
                          <th className="px-3 py-2">Direction</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Range</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kpis.map((kpi, i) => {
                          const status = getKpiStatus(kpi);
                          const unit = kpi.unit || '';
                          const hasBaseline = kpi.baseline != null;
                          const hasCurrent = kpi.currentValue != null;
                          const hasTarget = kpi.targetValue != null;
                          return (
                            <tr key={i} className="border-b border-border last:border-b-0">
                              <td className="px-3 py-2 font-medium">{kpi.name}</td>
                              <td className="px-3 py-2 text-right font-semibold">
                                {hasCurrent ? `${kpi.currentValue}${unit ? ` ${unit}` : ''}` : '--'}
                              </td>
                              <td className="px-3 py-2 text-right text-muted-foreground">
                                {hasTarget ? `${kpi.targetValue}${unit ? ` ${unit}` : ''}` : '--'}
                              </td>
                              <td className="px-3 py-2 font-semibold">
                                {getDirectionArrow(kpi.direction)} {kpi.direction || ''}
                              </td>
                              <td className="px-3 py-2">
                                <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', status.tone.chip)}>
                                  {status.label}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                {hasBaseline && hasTarget ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground">{kpi.baseline}</span>
                                    <div className="relative h-1.5 min-w-[60px] flex-1 overflow-hidden rounded-full bg-muted">
                                      {hasCurrent && (
                                        <div
                                          className={cn('absolute left-0 top-0 h-full transition-all', status.tone.bar)}
                                          style={{
                                            width: `${Math.min(100, Math.max(0, ((kpi.currentValue! - kpi.baseline!) / (kpi.targetValue! - kpi.baseline!)) * 100))}%`,
                                          }}
                                        />
                                      )}
                                    </div>
                                    <span className="text-xs text-muted-foreground">{kpi.targetValue}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">--</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : driver.kpis.length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">KPIs</p>
                    <div className="flex flex-col gap-0.5">
                      {driver.kpis.map((kpi, i) => (
                        <p key={i} className="text-sm">• {kpi}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No KPIs configured for this driver.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {canSeeAlignment && (
        <div className="mt-6">
          <h2 className="mb-3 text-lg font-semibold">Strategic Alignment (Cross-Executive)</h2>
          {alignmentLoading && (
            <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/3 animate-pulse bg-primary" />
            </div>
          )}
          {!alignmentLoading && alignmentByExec.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No cross-executive driver data available. This view requires multiple executives in your tenant.
              </CardContent>
            </Card>
          )}
          {!alignmentLoading && alignmentByExec.length > 0 && (
            <Card>
              <CardContent>
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Executive</th>
                        <th className="px-3 py-2">Driver</th>
                        <th className="px-3 py-2 text-right">Progress</th>
                        <th className="px-3 py-2">KPI Health</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alignmentData.map((row: any) => {
                        const isAligned = alignedDriverTitles.has(row.title.toLowerCase().trim());
                        return (
                          <tr
                            key={row.id}
                            className={cn(
                              'border-b border-border last:border-b-0',
                              isAligned && 'bg-primary/[0.06]',
                            )}
                          >
                            <td className="px-3 py-2 font-medium">{row.executiveName}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                {row.title}
                                {isAligned && (
                                  <span className="inline-flex items-center rounded-full border border-blue-500/60 bg-transparent px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-400">
                                    Aligned
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-1.5 w-[60px] overflow-hidden rounded-full bg-muted">
                                  <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, row.progressPercentage)}%` }} />
                                </div>
                                <p className="min-w-[36px] text-right text-sm font-semibold">{Math.round(row.progressPercentage)}%</p>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', getKpiHealthTone(row.kpiHealth))}>
                                {row.kpiHealth}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                                  row.lifecycleStatus === 'Active'
                                    ? 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400'
                                    : row.lifecycleStatus === 'Review'
                                      ? 'border-amber-500/60 text-amber-700 dark:text-amber-400'
                                      : 'border-border text-muted-foreground',
                                )}
                              >
                                {row.lifecycleStatus || 'Active'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {alignedDriverTitles.size > 0 && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Highlighted rows indicate drivers with matching titles across multiple executives.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Projects Tab
// ===========================================================================

function ProjectsTab({ drivers, workItems }: { drivers: AnnualStrategicDriver[]; workItems: WorkItem[] }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        setProjects(await projectsApi.getPortfolio());
      } catch (err) {
        console.error('Failed to load projects', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const ragCounts = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    return {
      Green: active.filter(p => p.ragStatus === 'Green').length,
      Amber: active.filter(p => p.ragStatus === 'Amber').length,
      Red: active.filter(p => p.ragStatus === 'Red').length,
    };
  }, [projects]);

  const timeByProject = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    const projectTimes = active.map(project => {
      const linkedItems = workItems.filter(wi => wi.projectId === project.id);
      const totalMinutes = linkedItems.reduce((sum, wi) => sum + wi.estimatedMinutes, 0);
      return { id: project.id, name: project.title, totalMinutes, totalHours: +(totalMinutes / 60).toFixed(1) };
    }).filter(p => p.totalMinutes > 0).sort((a, b) => b.totalMinutes - a.totalMinutes);

    const grandTotal = projectTimes.reduce((s, p) => s + p.totalMinutes, 0);
    return projectTimes.map(p => ({
      ...p,
      percentage: grandTotal > 0 ? Math.round((p.totalMinutes / grandTotal) * 100) : 0,
    }));
  }, [projects, workItems]);

  const alignment = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    const driverIdsWithProjects = new Set(active.map(p => p.strategicDriverId).filter(Boolean));
    const withProjects = drivers.filter(d => driverIdsWithProjects.has(d.id));
    const withoutProjects = drivers.filter(d => !driverIdsWithProjects.has(d.id));
    return { withProjects, withoutProjects };
  }, [projects, drivers]);

  const staleProjects = useMemo(() => {
    const active = projects.filter(p => !p.isArchived && p.status !== 'Completed' && p.status !== 'Cancelled');
    return active.map(project => {
      const linkedItems = workItems.filter(wi => wi.projectId === project.id);
      const latestWorkItemUpdate = linkedItems.length > 0
        ? new Date(Math.max(...linkedItems.map(wi => new Date(wi.updatedAt).getTime())))
        : null;
      const projectUpdate = new Date(project.updatedAt);
      const lastActivity = latestWorkItemUpdate && latestWorkItemUpdate > projectUpdate ? latestWorkItemUpdate : projectUpdate;
      const daysSinceActivity = Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
      return { ...project, lastActivity, daysSinceActivity };
    }).filter(p => p.daysSinceActivity >= 14).sort((a, b) => b.daysSinceActivity - a.daysSinceActivity);
  }, [projects, workItems]);

  const ragTone = (rag: string) => {
    if (rag === 'Green') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    if (rag === 'Amber') return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
    return 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
  };

  return (
    <div>
      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}

      {!loading && projects.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderOpen className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No projects found</p>
            <p className="mt-1 text-sm text-muted-foreground">Create projects to see portfolio reporting here.</p>
          </CardContent>
        </Card>
      )}

      {!loading && projects.length > 0 && (
        <div className="flex flex-col gap-4">
          {/* Portfolio RAG */}
          <div>
            <h3 className="mb-3 text-base font-semibold">Portfolio RAG Summary</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { label: 'Green', count: ragCounts.Green, text: 'text-emerald-600 dark:text-emerald-400' },
                { label: 'Amber', count: ragCounts.Amber, text: 'text-amber-700 dark:text-amber-400' },
                { label: 'Red', count: ragCounts.Red, text: 'text-rose-600 dark:text-rose-400' },
              ].map(it => (
                <Card key={it.label}>
                  <CardContent className="py-5 text-center">
                    <p className={cn('text-4xl font-bold', it.text)}>{it.count}</p>
                    <p className="mt-1 text-sm font-semibold text-muted-foreground">{it.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Time Allocation by Project */}
          <Card>
            <CardContent>
              <h3 className="mb-3 text-base font-semibold">Time Allocation by Project</h3>
              {timeByProject.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Project</th>
                        <th className="px-3 py-2 text-right">Hours</th>
                        <th className="w-2/5 px-3 py-2">Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeByProject.map(p => (
                        <tr key={p.id} className="border-b border-border last:border-b-0">
                          <td className="px-3 py-2 font-medium">{p.name}</td>
                          <td className="px-3 py-2 text-right font-semibold">{p.totalHours}h</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                                <div className="h-full bg-primary transition-all" style={{ width: `${p.percentage}%` }} />
                              </div>
                              <p className="min-w-[32px] text-right text-xs font-semibold text-muted-foreground">{p.percentage}%</p>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">No time tracked against projects yet</p>
              )}
            </CardContent>
          </Card>

          {/* Strategic Alignment */}
          <Card>
            <CardContent>
              <h3 className="mb-3 text-base font-semibold">Strategic Alignment</h3>
              {drivers.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                      Drivers with Projects ({alignment.withProjects.length})
                    </p>
                    {alignment.withProjects.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {alignment.withProjects.map(d => (
                          <div key={d.id} className="flex items-center gap-2">
                            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                            <p className="text-sm">{d.title}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">None</p>
                    )}
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
                      Drivers without Projects ({alignment.withoutProjects.length})
                    </p>
                    {alignment.withoutProjects.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {alignment.withoutProjects.map(d => (
                          <div key={d.id} className="flex items-center gap-2">
                            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                            <p className="text-sm">{d.title}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">None</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">No strategic drivers configured</p>
              )}
            </CardContent>
          </Card>

          {/* Stale projects */}
          <Card>
            <CardContent>
              <h3 className="text-base font-semibold">Stale Project Detection</h3>
              <p className="mb-3 text-sm text-muted-foreground">Projects with no activity in the last 14 days</p>
              {staleProjects.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Project</th>
                        <th className="px-3 py-2">RAG</th>
                        <th className="px-3 py-2">Last Activity</th>
                        <th className="px-3 py-2 text-right">Days Stale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staleProjects.map(p => (
                        <tr key={p.id} className="border-b border-border last:border-b-0">
                          <td className="px-3 py-2 font-medium">{p.title}</td>
                          <td className="px-3 py-2">
                            <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', ragTone(p.ragStatus))}>
                              {p.ragStatus}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{p.lastActivity.toLocaleDateString()}</td>
                          <td className={cn(
                            'px-3 py-2 text-right font-semibold',
                            p.daysSinceActivity >= 30 ? 'text-rose-600 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400',
                          )}>
                            {p.daysSinceActivity}d
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">All projects have recent activity</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Time Allocation Tab
// ===========================================================================

function TimeAllocationTab() {
  const [report, setReport] = useState<TimeAllocationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { start, end } = getDateRange(dateRange);
        const from = start.toISOString().split('T')[0];
        const to = end.toISOString().split('T')[0];
        const data = await reportingApi.getTimeAllocation(from, to);
        setReport(data);
      } catch (err) {
        console.error('Failed to load time allocation', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dateRange]);

  const toHours = (minutes: number) => Math.round((minutes / 60) * 10) / 10;

  const meetingVsFocus = useMemo(() => {
    if (!report) return [];
    return [
      { name: 'Meetings', value: report.totalMeetingMinutes, color: C.secondary, bar: 'bg-[#CE93D8]', text: 'text-[#CE93D8]' },
      { name: 'Focus Time', value: report.totalFocusMinutes, color: '#6C9BFF', bar: 'bg-[#6C9BFF]', text: 'text-[#6C9BFF]' },
    ].filter(d => d.value > 0);
  }, [report]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Time Allocation Analytics</h2>
        <RangeToggle value={dateRange} onChange={setDateRange} />
      </div>

      <HeadlineInsight result={deriveTimeAllocationInsight(report)} />

      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}

      {!loading && report && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {meetingVsFocus.length > 0 && (
            <Card className="h-full">
              <CardContent>
                <h3 className="mb-3 text-base font-semibold">Meeting vs Focus Time</h3>
                <div className="flex flex-col gap-3">
                  {meetingVsFocus.map(item => (
                    <div key={item.name}>
                      <div className="mb-1 flex justify-between">
                        <p className="text-sm font-medium text-muted-foreground">{item.name}</p>
                        <p className={cn('text-sm font-semibold', item.text)}>{toHours(item.value)}h</p>
                      </div>
                      <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full transition-all', item.bar)}
                          style={{ width: `${report.totalMinutes > 0 ? Math.min(100, (item.value / report.totalMinutes) * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">Total tracked: {toHours(report.totalMinutes)}h</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Driver */}
          <Card className="h-full">
            <CardContent>
              <h3 className="mb-3 text-base font-semibold">By Strategic Driver</h3>
              {report.byDriver.length > 0 ? (
                <SimpleHourShareTable rows={report.byDriver.map(d => ({ key: d.driverId, name: d.driverName, hours: toHours(d.minutes), pct: d.percentage }))} barClass="bg-primary" />
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">No completed work linked to strategic drivers in this period</p>
              )}
            </CardContent>
          </Card>

          {/* By Project */}
          <Card className="h-full">
            <CardContent>
              <h3 className="mb-3 text-base font-semibold">By Project</h3>
              {report.byProject.length > 0 ? (
                <SimpleHourShareTable rows={report.byProject.map(p => ({ key: p.projectId, name: p.projectName, hours: toHours(p.minutes), pct: p.percentage }))} barClass="bg-[#CE93D8]" />
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">No completed work linked to projects in this period</p>
              )}
            </CardContent>
          </Card>

          {/* By Work Type */}
          <Card className="h-full">
            <CardContent>
              <h3 className="mb-3 text-base font-semibold">By Work Type</h3>
              {report.byType.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">Hours</th>
                        <th className="w-[30%] px-3 py-2">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byType.map(t => (
                        <tr key={t.type} className="border-b border-border last:border-b-0">
                          <td className="px-3 py-2">
                            <span
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                              style={{ backgroundColor: `${TYPE_COLORS[t.type] || '#6C9BFF'}20`, color: TYPE_COLORS[t.type] || '#6C9BFF' }}
                            >
                              {t.type}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">{t.count}</td>
                          <td className="px-3 py-2 text-right font-semibold">{toHours(t.minutes)}h</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                                <div className="h-full transition-all" style={{ width: `${t.percentage}%`, backgroundColor: TYPE_COLORS[t.type] || '#6C9BFF' }} />
                              </div>
                              <p className="min-w-[32px] text-right text-xs font-semibold text-muted-foreground">{t.percentage}%</p>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">No completed work items in this period</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!loading && !report && (
        <Card>
          <CardContent className="py-12 text-center">
            <PieIcon className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No time allocation data available</p>
            <p className="mt-1 text-sm text-muted-foreground">Complete work items to see time allocation analytics here.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SimpleHourShareTable({
  rows,
  barClass,
}: {
  rows: { key: string; name: string; hours: number; pct: number }[];
  barClass: string;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2 text-right">Hours</th>
            <th className="w-[35%] px-3 py-2">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2 font-medium">{r.name}</td>
              <td className="px-3 py-2 text-right font-semibold">{r.hours}h</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className={cn('h-full transition-all', barClass)} style={{ width: `${r.pct}%` }} />
                  </div>
                  <p className="min-w-[32px] text-right text-xs font-semibold text-muted-foreground">{r.pct}%</p>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// Activity Feed Tab
// ===========================================================================

function getActivityConfig(type: string): { icon: React.ComponentType<{ className?: string }>; tone: string; label: string } {
  switch (type) {
    case 'WorkItemCompleted':
      return { icon: CheckCircle2, tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', label: 'Completed' };
    case 'WorkItemCreated':
      return { icon: PlusCircle, tone: 'bg-primary/15 text-primary', label: 'Created' };
    case 'PlanCreated':
      return { icon: CalendarDays, tone: 'bg-blue-500/15 text-blue-700 dark:text-blue-400', label: 'Plan' };
    case 'KpiRecorded':
      return { icon: LineChart, tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', label: 'KPI' };
    case 'ReflectionAdded':
      return { icon: Pencil, tone: 'bg-purple-500/15 text-purple-700 dark:text-purple-400', label: 'Reflection' };
    case 'ProjectUpdated':
      return { icon: FolderHeart, tone: 'bg-blue-600/15 text-blue-800 dark:text-blue-300', label: 'Project' };
    default:
      return { icon: Activity, tone: 'bg-muted text-muted-foreground', label: type };
  }
}

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function ActivityFeedTab() {
  const [feed, setFeed] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await reportingApi.getActivityFeed(50);
        setFeed(data);
      } catch (err) {
        console.error('Failed to load activity feed', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">Recent Activity</h2>
      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}
      {!loading && feed.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <History className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No recent activity</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Activity will appear here as you create work items, record KPIs, and complete plans.
            </p>
          </CardContent>
        </Card>
      )}
      {!loading && feed.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col">
              {feed.map((item, i) => {
                const config = getActivityConfig(item.type);
                const Icon = config.icon;
                return (
                  <div
                    key={`${item.entityId}-${item.type}-${i}`}
                    className="flex items-center gap-3 border-b border-border px-5 py-3 last:border-b-0 hover:bg-muted/20"
                  >
                    <div className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-full', config.tone)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.entityType}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', config.tone)}>
                        {config.label}
                      </span>
                      <p className="min-w-[55px] whitespace-nowrap text-right text-xs text-muted-foreground">
                        {formatRelativeTime(item.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===========================================================================
// Delegation Tab
// ===========================================================================

function DelegationTab() {
  const [report, setReport] = useState<DelegationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { start, end } = getDateRange(range);
        const from = start.toISOString().slice(0, 10);
        const to = end.toISOString().slice(0, 10);
        const data = await reportingApi.getDelegationReport(from, to);
        setReport(data);
      } catch (err) {
        console.error('Failed to load delegation report', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [range]);

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <RangeToggle value={range} onChange={setRange} />
      </div>
      <HeadlineInsight result={deriveDelegationInsight(report)} />
      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}
      {!loading && report && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <SummaryStat value={report.totalDelegated} label="Total Delegated" tone="text-primary" />
            <SummaryStat value={`${report.completionRate}%`} label="Completion Rate" tone="text-emerald-600 dark:text-emerald-400" />
            <SummaryStat value={report.completed} label="Completed" tone="text-[#CE93D8]" />
            <SummaryStat value={`${report.averageTurnaroundDays}d`} label="Avg Turnaround" tone="text-amber-700 dark:text-amber-400" />
          </div>
          <Card>
            <CardContent>
              <h3 className="mb-3 text-base font-semibold">By Delegatee</h3>
              {report.byDelegatee.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2 text-right">Received</th>
                        <th className="px-3 py-2 text-right">Completed</th>
                        <th className="px-3 py-2 text-right">Completion Rate</th>
                        <th className="px-3 py-2 text-right">Avg Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byDelegatee.map(d => (
                        <tr key={d.userId} className="border-b border-border last:border-b-0">
                          <td className="px-3 py-2 font-medium">{d.name}</td>
                          <td className="px-3 py-2 text-right">{d.received}</td>
                          <td className="px-3 py-2 text-right">{d.completed}</td>
                          <td className="px-3 py-2 text-right">
                            {d.received > 0 ? Math.round((d.completed / d.received) * 100) : 0}%
                          </td>
                          <td className="px-3 py-2 text-right">{d.avgDays}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">No delegated items found for this period</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {!loading && !report && (
        <Card>
          <CardContent className="py-12 text-center">
            <ArrowRightLeft className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No delegation data available</p>
            <p className="mt-1 text-sm text-muted-foreground">Delegate work items to see effectiveness metrics here.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryStat({ value, label, tone }: { value: React.ReactNode; label: string; tone: string }) {
  return (
    <Card>
      <CardContent className="py-5 text-center">
        <p className={cn('text-3xl font-bold', tone)}>{value}</p>
        <p className="mt-1 text-sm text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Cross-Executive Tab
// ===========================================================================

function CrossExecutiveTab() {
  const [crossData, setCrossData] = useState<any>(null);
  const [forecast, setForecast] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('month');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { start, end } = getDateRange(dateRange);
        const from = start.toISOString().split('T')[0];
        const to = end.toISOString().split('T')[0];
        const [crossResult, forecastResult] = await Promise.allSettled([
          reportingApi.getCrossExecutiveReport(from, to),
          reportingApi.getCapacityForecast(4),
        ]);
        if (crossResult.status === 'fulfilled') setCrossData(crossResult.value);
        if (forecastResult.status === 'fulfilled') setForecast(forecastResult.value);
        if (crossResult.status === 'rejected' && forecastResult.status === 'rejected') {
          setError('Failed to load cross-executive reports.');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dateRange]);

  const executives: any[] = crossData?.executives || crossData?.reports || (Array.isArray(crossData) ? crossData : []);
  const forecastWeeks: any[] = forecast?.weeks || (Array.isArray(forecast) ? forecast : []);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Cross-Executive Overview</h2>
        <RangeToggle value={dateRange} onChange={setDateRange} />
      </div>

      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}
      {error && (
        <Card className="mb-3">
          <CardContent className="py-6 text-center text-sm text-muted-foreground">{error}</CardContent>
        </Card>
      )}

      {!loading && executives.length > 0 && (
        <Card className="mb-4">
          <CardContent>
            <h3 className="mb-3 text-base font-semibold">Executive Comparison</h3>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Executive</th>
                    <th className="px-3 py-2 text-right">Completion Rate</th>
                    <th className="px-3 py-2 text-right">Focus Time Ratio</th>
                    <th className="px-3 py-2 text-right">Carry-Forward</th>
                    <th className="px-3 py-2 text-right">Strategic Alignment</th>
                  </tr>
                </thead>
                <tbody>
                  {executives.map((exec: any, i: number) => {
                    const completionRate = exec.completionRate ?? exec.completionPercent ?? 0;
                    const focusRatio = exec.focusTimeRatio ?? exec.focusTimePercent ?? 0;
                    const carryForward = exec.carryForwardCount ?? exec.carryForwards ?? 0;
                    const strategicPct = exec.strategicAlignmentPercent ?? exec.strategicAlignment ?? 0;
                    return (
                      <tr key={exec.executiveUserId || exec.executiveName || i} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-2 font-medium">
                          {exec.executiveName || exec.displayName || exec.email || `Executive ${i + 1}`}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-2">
                            <div className="h-1.5 w-[60px] overflow-hidden rounded-full bg-muted">
                              <div
                                className={cn(
                                  'h-full transition-all',
                                  completionRate >= 70 ? 'bg-emerald-500' : completionRate >= 40 ? 'bg-amber-500' : 'bg-rose-500',
                                )}
                                style={{ width: `${Math.min(completionRate, 100)}%` }}
                              />
                            </div>
                            <p className="min-w-[40px] text-right text-sm font-semibold">{Math.round(completionRate)}%</p>
                          </div>
                        </td>
                        <td className={cn(
                          'px-3 py-2 text-right font-semibold',
                          focusRatio >= 30 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400',
                        )}>
                          {Math.round(focusRatio)}%
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                            carryForward > 5 ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400' : 'bg-muted text-foreground',
                          )}>
                            {carryForward}
                          </span>
                        </td>
                        <td className={cn(
                          'px-3 py-2 text-right font-semibold',
                          strategicPct >= 50 ? 'text-primary' : 'text-muted-foreground',
                        )}>
                          {Math.round(strategicPct)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && executives.length === 0 && !error && (
        <Card className="mb-4">
          <CardContent className="py-12 text-center">
            <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">No cross-executive data available</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Cross-executive reports require multiple executives with active plans.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && forecastWeeks.length > 0 && (
        <Card>
          <CardContent>
            <h3 className="text-base font-semibold">Capacity forecast: next 4 weeks</h3>
            <p className="mb-3 text-sm text-muted-foreground">Planned minutes vs available minutes per week</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={forecastWeeks} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.gridLine} />
                <XAxis
                  dataKey="weekLabel"
                  tick={{ fill: C.textSecondary, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: string) => value || ''}
                />
                <YAxis
                  tick={{ fill: C.textSecondary, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => `${Math.round(value / 60)}h`}
                />
                <RechartsTooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="rounded-lg border border-border bg-background p-2 text-sm">
                        <p className="mb-1 text-xs text-muted-foreground">{label}</p>
                        {payload.map((entry: any, i: number) => (
                          <p key={i} className="text-sm" style={{ color: entry.color }}>
                            {entry.name}: {Math.round(entry.value / 60)}h ({entry.value}m)
                          </p>
                        ))}
                      </div>
                    );
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                />
                <Legend formatter={(value) => <span style={{ color: C.textSecondary, fontSize: 12 }}>{value}</span>} />
                <Bar dataKey="availableMinutes" name="Available" fill="#6C9BFF" radius={[4, 4, 0, 0]} barSize={32} fillOpacity={0.4} />
                <Bar dataKey="plannedMinutes" name="Planned" fill={C.secondary} radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
